import React from 'react';
import { Link } from 'react-router-dom';
import { PropTypes } from 'prop-types';

import blanks from 'components/Blanks/blanks.json';

export function buildDescription({ codeDescription, context }) {
  // check description for dynamic parts like ${...}
  const interpolationParts = codeDescription.match(/\${[^}\r\n]*}/g);
  if (interpolationParts && interpolationParts.length) {
    let result = codeDescription;
    interpolationParts.forEach((template) => {
      if (template != null) {
        const matches = template.match(/\${([^}]*)}/);
        const contextField = matches != null && matches[1] != null;
        if (contextField != null) {
          result = result.split(template).join(context[contextField]);
        }
      }
    });
    return result;
  }
  return codeDescription;
}

function isOverridden(overrides, code) {
  return overrides != null && overrides.current != null && overrides.current.rejections != null && overrides.current.rejections.filter(r => r.code === code).length > 0;
}

function isValid(calculateValidity, rejections, overrides, valid) {
  if (calculateValidity
    && overrides != null
    && overrides.current != null) {
    if (overrides.current.formOverride != null && overrides.current.formOverride.override) {
      return overrides.current.formOverride.valid;
    }

    if (overrides.current.rejections != null) {
      const overrideCodes = overrides.current.rejections.map(a => a != null && a.code);
      return rejections.filter(item => item != null && item.code !== 'REJ500000' && overrideCodes.indexOf(item.code) === -1).length === 0;
    }
  }
  return valid;
}

function hasOverriddenRejection(code, rejections, overrides) {
  if (rejections != null && overrides != null && overrides.current != null && overrides.current.rejections != null) {
    const numericCode = code.replace('REC', '').replace('WRN', '');
    const isInRejections = rejections.filter(item => item.code.replace('REJ', '') === numericCode).length !== 0;
    const isInOverrides = overrides.current.rejections.filter(item => item.code.replace('REJ', '') === numericCode).length !== 0;

    return isInRejections && isInOverrides;
  }
  return false;
}

function overriddenToInvalid(calculateValidity, overrides) {
  return overrides != null
  && overrides.current != null
  && overrides.current.formOverride != null
  && overrides.current.formOverride.override
  && ((!calculateValidity && !overrides.current.formOverride.isDraft) || (calculateValidity && overrides.current.formOverride.isDraft))
  && !overrides.current.formOverride.valid;
}

function overriddenToInvalidText(overrides) {
  return (overrides
    && overrides.current != null
    && overrides.current.formOverride != null
    && overrides.current.formOverride.override)
    ? overrides.current.formOverride.comments
    : '';
}

function validityDisplayText(valid, treaty, locked) {
  let statusText = '';
  statusText = valid ? 'Valid' : 'Invalid';
  statusText += treaty ? ' with Treaty' : '';
  statusText += locked ? ' & Locked' : '';

  return statusText;
}

function getDisplayTextForName(formType) {
  if (formType.indexOf('CRSCP') === -1) {
    return '| Name:';
  }
  return '| Controlling Person Name:';
}

const copyAccountNumber = (accountNumber) => {
  const textField = document.createElement('textarea');
  textField.innerText = accountNumber;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
};

const IndividualValidationResult = ({
  calculateValidity,
  valid,
  rejections,
  warnings,
  overrides,
  formType,
  formId,
  customerId,
  customerName,
  isOnlineForm,
  isWithTreaty,
  isLocked,
  accountNumber
}) => (
  /* eslint-disable max-len, react/no-array-index-key */
  <div className="single-validation-result">
    <div className="result-header">
      <Link to={ `/forms/${ formType.toLowerCase() }/${ formId }` }>{ blanks[formType.toLowerCase()] }
        { (customerId != null && customerId.length > 0) ? ` - Customer ID: ${ customerId }` : '' }
        { (customerName != null && customerName.length > 0) ? ` ${ getDisplayTextForName(formType) } ${ customerName }` : '' }
      </Link>
    </div>

    { accountNumber != null && <div className="copy-account-number">
      Account Number: { accountNumber } <button type="button" className="icon icon-copy" onClick={ () => { copyAccountNumber(accountNumber); } } />
    </div> }

    { isValid(calculateValidity, rejections, overrides, valid) && <h2 className="valid"> { validityDisplayText(true, isWithTreaty, isLocked) } </h2> }
    { !isValid(calculateValidity, rejections, overrides, valid) && <h2 className="invalid"> { validityDisplayText(false, isWithTreaty, isLocked) } </h2> }
    { isValid(calculateValidity, rejections, overrides, valid) && <div className="valid-block">
      { !overriddenToInvalid(calculateValidity, overrides) && <span>The form is valid</span> }
      { isOnlineForm && (!warnings || warnings.length === 0) && <span> and does not have any warnings or rejections.</span> }
      { warnings && warnings.length !== 0 && !overriddenToInvalid(calculateValidity, overrides) && <span>, but it has some warnings / instructions.</span> }
      { warnings && warnings.length === 0 && !overriddenToInvalid(calculateValidity, overrides) && '.' }
    </div> }
    { ((rejections && rejections.length !== 0) || (rejections.length === 0 && overriddenToInvalid(calculateValidity, overrides))) && <div className="result-block">

      { formType.indexOf('IMY') === -1
        && <React.Fragment>
          <div className="heading-row">Rejections:</div>
          <div className="reasons-for-rejection-list">
            <ul>
              { overriddenToInvalid(calculateValidity, overrides) && <li>
                <em>Client specific treatment, specified below.</em>
                &nbsp;(<strong>REJ999999</strong>)
              </li> }
              { rejections.map(rejection => rejection != null && (
                <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                  <em>{ buildDescription(rejection) }</em>
                  &nbsp;(<strong>{ rejection.code }</strong>)
                </li>
              )) }
            </ul>
          </div>
        </React.Fragment> }
      { formType.indexOf('IMY') > -1 && <React.Fragment>
        { rejections.some(rejection => rejection.code.substring(3) < 500000 && rejection.code.substring(3) !== 999999)
        && <React.Fragment>
          <div className="heading-row">Form Rejections:</div>
          <div className="reasons-for-rejection-list">
            <ul>
              { overriddenToInvalid(calculateValidity, overrides) && <li>
                <em>Client specific treatment, specified below.</em>
                &nbsp;(<strong>REJ999999</strong>)
              </li> }
              { rejections.map(rejection => rejection != null && rejection.code.substring(3) < 500000 && (
                <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                  <em>{ buildDescription(rejection) }</em>
                  &nbsp;(<strong>{ rejection.code }</strong>)
                </li>
              )) }
            </ul>
          </div>
        </React.Fragment> }
        { rejections.some(rejection => rejection.code.substring(3) >= 500000 && rejection.code.substring(3) < 999999)
        && <React.Fragment>
          <br />
          <div className="heading-row">IMY Structure Rejections:</div>
          <div className="reasons-for-rejection-list">
            <ul>
              { rejections.map(rejection => rejection != null && rejection.code.substring(3) >= 500000 && rejection.code.substring(3) < 999999 && (
                <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                  <em>{ buildDescription(rejection) }</em>
                  &nbsp;(<strong>{ rejection.code }</strong>)
                </li>
              )) }
            </ul>
          </div>
        </React.Fragment> }
      </React.Fragment> }
    </div> }
    { ((warnings && warnings.length !== 0) || (warnings.length === 0 && overriddenToInvalid(calculateValidity, overrides))) && <div className="result-block">
      { isOnlineForm && <div className="heading-row" style={ { fontWeight: 'bold', marginBottom: '1rem' } }>Warnings:</div> }
      { !isOnlineForm && warnings.filter(w => w.code.indexOf('WRN') > -1).length > 0 && <div className="heading-row">Warnings:</div> }
      <div className="red-flags-and-recs-list">
        <ul>
          { overriddenToInvalid(calculateValidity, overrides) && <li className="red-flag">
            <em>{ overriddenToInvalidText(overrides) }</em>
            &nbsp;(<strong>WRN999999</strong>)
          </li> }
          { warnings.filter(w => w.code.indexOf('WRN') > -1).map(warning => warning != null && (
            <li
              key={ warning.key }
              className={ `red-flag ${ hasOverriddenRejection(warning.code, rejections, overrides) ? 'disabled' : '' }` }
            >
              <em>{ buildDescription(warning) }</em>
              &nbsp;(<strong>{ warning.code }</strong>)
            </li>
          )) }
        </ul>
      </div>
    </div> }

    { ((warnings && warnings.length !== 0) || (warnings.length === 0 && overriddenToInvalid(calculateValidity, overrides))) && <div className="result-block">
      { isOnlineForm && <div className="heading-row" style={ { fontWeight: 'bold', marginBottom: '1rem' } }>Instructions:</div> }
      { !isOnlineForm && <div className="heading-row">Instructions:</div> }
      <div className="red-flags-and-recs-list">
        <ul>
          { warnings.filter(w => w.code.indexOf('WRN') === -1).map(warning => warning != null && (
            <li
              key={ warning.key }
              className={ `recommendation ${ hasOverriddenRejection(warning.code, rejections, overrides) ? 'disabled' : '' }` }
            >
              <em>{ buildDescription(warning) }</em>
              &nbsp;(<strong>{ warning.code }</strong>)
            </li>
          )) }
        </ul>
      </div>
    </div> }
  </div>
  /* eslint-enable max-len, react/no-array-index-key */
);

IndividualValidationResult.propTypes = {
  valid: PropTypes.bool.isRequired,
  rejections: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    codeDescription: PropTypes.string
  })).isRequired,
  warnings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  overrides: PropTypes.shape({
    current: PropTypes.shape({}),
    draft: PropTypes.shape({})
  }),
  formType: PropTypes.string.isRequired,
  formId: PropTypes.number.isRequired,
  customerId: PropTypes.string,
  customerName: PropTypes.string,
  isOnlineForm: PropTypes.bool.isRequired,
  calculateValidity: PropTypes.bool,
  isWithTreaty: PropTypes.bool,
  isLocked: PropTypes.bool,
  accountNumber: PropTypes.string
};

IndividualValidationResult.defaultProps = {
  customerId: '',
  customerName: '',
  overrides: null,
  calculateValidity: false,
  isWithTreaty: false,
  isLocked: false,
  accountNumber: null
};

export default IndividualValidationResult;
